/* eslint-disable no-nested-ternary */
import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { updatePlanModal, updateNavigationTourGuide } from 'store/user';
import dayjs from 'dayjs';
import { Space, Popover, Typography, Button } from 'antd';
import { DownOutlined, EyeOutlined } from '@ant-design/icons';
import Joyride, { STATUS, EVENTS, ACTIONS } from 'react-joyride';
import styled from 'styled-components';
import { TRANSACTION_SEARCH_LIST, TRANSACTION_FILTER_LIST } from 'constants/transaction';
import EbzTable from 'components/EbzTable';
import EbzButton from 'components/EbzButton';
import EbzFilterGroup from 'components/EbzFilterGroup';
import currencyFormat from 'utils/currencyFormat';
import transactionsAPI from 'services/transactions';
import merchantAPI from 'services/merchants';
import TransactionSummaryCard from 'components/TransactionSummaryCard';
import transactionsBlur from 'assets/images/transactions_table.webp';
import CustomSkeleton from 'components/CustomSkeleton';
import trackingService from 'services/trackingService';
import EVENT_NAME from 'utils/const';
import joyrideCustomTooltip from 'components/JoyrideComponent/JoyrideTooltip';
import TransactionDetailsDrawer from './TransactionDetailsDrawer';
import { TOUR_TRANSACTION_CONTENT } from './const';

const EbzPopover = styled(Popover)`
  //popover
  .ant-popover-arrow {
    display: none !important;
  }

  .ant-popover-content {
    position: relative;
    top: -10px;
  }
`;

function TransactionListPage() {
  // State
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    nextCursor: null,
    prevCursor: null,
    pageNumber: 1
  });
  const [filtersObj, setFiltersObj] = useState({});
  const [currentView, setCurrentView] = useState([]);
  const [viewMoreModal, setViewMoreModal] = useState(false);
  const [transactionSummary, setTransactionSummary] = useState({});
  const [isTransactionSummary, setIsTransactionSummary] = useState(false);
  const { nextCursor, prevCursor, pageNumber } = pagination;
  const [tourGuideRun, setTourGuideRun] = useState(false);
  const [tourGuideSteps, setTourGuideSteps] = useState([]);
  const [tourStepIndex, setTourStepIndex] = useState(1);
  const tourGuide = useSelector((state) => state.user.navigationTourGuidetails);

  const fetchTransactionsSummary = async () => {
    try {
      const response = await transactionsAPI.transaction_summary();
      if (!response.success) {
        console.error(
          'Something went wrong while fetching partner transaction transactionSummary.'
        );
        return;
      }
      setTransactionSummary(response?.data);
    } catch (error) {
      console.error('Error occurred while fetching transactions:', error);
    } finally {
      setIsTransactionSummary(true);
    }
  };

  const isTransacationsAllowed = useSelector(
    (state) => state.user.planPermissions.can_view_transactions
  );
  const productList = useSelector((state) => state?.user?.productList);
  const planModal = useSelector((state) => state.user.planModal);
  const dispatch = useDispatch();
  const togglePlanModal = () => {
    dispatch(updatePlanModal(!planModal));
  };

  const fetchTransactions = async (type = 'next') => {
    setLoading(true);
    try {
      const newPagination =
        type === 'next'
          ? { next_cursor: nextCursor, page_number: pageNumber }
          : { prev_cursor: prevCursor, page_number: pageNumber };

      const response = await transactionsAPI.transactions({
        ...newPagination,
        ...filtersObj
      });

      if (!response.success) {
        console.error('Something went wrong while fetching transactions.');
        return;
      }
      setTransactions([]);

      if (response?.data?.merchants?.length === 0) {
        setPagination({
          nextCursor: null,
          prevCursor: response?.data?.prev_cursor || null,
          pageNumber: response?.data?.page_number || 1
        });
      }
      setTransactions(response?.data?.merchants);

      if (response?.data?.merchants?.length === response?.data?.page_size) {
        setPagination({
          nextCursor: response?.data?.next_cursor,
          prevCursor: response?.data?.prev_cursor,
          pageNumber: response?.data?.page_number
        });
      } else {
        setPagination({
          nextCursor: null,
          prevCursor: response?.data?.prev_cursor,
          pageNumber: response?.data?.page_number
        });
      }
    } catch (error) {
      console.error('Error occurred while fetching transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isTransacationsAllowed) {
      fetchTransactionsSummary();
      fetchTransactions();
    } else {
      navigate('/referrals');
    }
  }, [filtersObj, isTransacationsAllowed]);

  // Handle table pagination and filtering
  const handleTableChange = (filter) => {
    setFiltersObj(filter);
    setPagination((prevState) => ({
      ...prevState,
      nextCursor: null,
      prevCursor: null,
      pageNumber: 1
    }));
    fetchTransactions();
  };

  // Apply filters callback function
  const applyFiltersCallback = (req) => {
    const [searchParams, checkboxGroup, date, dateRange, amount] = req;
    const filters = {
      ...searchParams,
      ...checkboxGroup,
      ...dateRange,
      ...amount,
      ...date
    };

    setFiltersObj(filters);
    setPagination((prevState) => ({
      ...prevState,
      nextCursor: null,
      prevCursor: null,
      pageNumber: 1
    }));
  };

  // Clear filters callback function
  const clearFiltersCallback = () => {
    setFiltersObj({});
    setPagination((prevState) => ({
      ...prevState,
      nextCursor: null,
      prevCursor: null,
      pageNumber: 1
    }));
  };

  const handleViewMore = (e) => {
    setViewMoreModal(e);
    setCurrentView(null);
  };

  const handleViewDetails = (transactionInfo) => {
    handleViewMore(true);
    setCurrentView(transactionInfo);
  };
  const columnStyles = {
    fontWeight: 'bolder',
    fontSize: '16px',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    textAlign: 'left'
  };

  const columns = [
    {
      title: <Space style={columnStyles}>Easebuzz ID</Space>,
      dataIndex: 'app_transaction_id',
      key: 'app_transaction_id',
      width: '190px'
    },
    {
      title: <Space style={columnStyles}>Merchant ID</Space>,
      dataIndex: 'merchant_id',
      align: 'left',
      key: 'merchant_id'
    },
    {
      title: <Space style={columnStyles}>Merchant Name</Space>,
      dataIndex: 'merchant_name',
      key: 'merchant_name',
      render: (e) => {
        return e ?? 'NA';
      }
    },
    {
      title: <Space style={columnStyles}> Date of Transaction</Space>,
      dataIndex: 'transaction_ist_date',
      align: 'left',

      key: 'transaction_ist_date',
      render: (e) => {
        return e === null ? 'Invalid Date' : dayjs(e).format('DD MMM, YYYY');
      }
    },
    {
      title: <Space style={columnStyles}>Transaction Amount</Space>,
      dataIndex: 'transaction_amount',
      key: 'transaction_amount',
      render: (e) => {
        return (
          <Typography.Text style={{ wordSpacing: '6px' }}>
            {currencyFormat(e, true, true)}
          </Typography.Text>
        );
      }
    },
    {
      title: <Space style={columnStyles}>Payment Mode</Space>,
      dataIndex: 'transaction_type',
      key: 'transaction_type'
    },
    {
      title: <Space style={columnStyles}>Transaction Ref. No.</Space>,
      dataIndex: 'transaction_ref_number',
      key: 'transaction_ref_number',
      ellipsis: {
        showTitle: true
      }
    },
    {
      title: <Space style={columnStyles}>Bank Ref. No.</Space>,
      dataIndex: 'bank_ref_number',
      key: 'bank_ref_number',
      ellipsis: {
        showTitle: true
      }
    },
    {
      title: <Space style={columnStyles}>Commission Amount</Space>,
      dataIndex: 'partner_commission_amount',
      key: 'partner_commission_amount',
      // width: '10px',
      render: (e) => {
        return currencyFormat(e, true, true);
      }
    },
    {
      title: <Space style={columnStyles}>Details</Space>,
      align: 'Center',
      render: (transactionInfo) => {
        return (
          <EyeOutlined
            style={{ color: 'var(--color-primary)', cursor: 'pointer', fontSize: '20px' }}
            onClick={() => handleViewDetails(transactionInfo)}
          />
        );
      }
    }
  ];

  const fetchAndUpdateNavigation = async (paramKey) => {
    const request = {
      update: paramKey
    };
    const response = await merchantAPI.fetchNavigationFlag(request);
    if (response.success) {
      dispatch(updateNavigationTourGuide(response?.data));
    } else {
      console.log('something went wrong');
    }
  };

  const handleJoyrideCallback = (callbackProps) => {
    const { status, index, type, action, size } = callbackProps;

    if ([EVENTS.STEP_AFTER, EVENTS.TARGET_NOT_FOUND].includes(type)) {
      if ([ACTIONS.NEXT].includes(action)) {
        const nextIndex = index + 1;
        setTourStepIndex(nextIndex);
      } else if ([ACTIONS.PREV].includes(action) && index === 1) {
        setTourStepIndex(1);
      } else {
        const prevIndex = index - 1;
        setTourStepIndex(prevIndex);
      }
      if (index === 1 && [ACTIONS.PREV].includes(action)) {
        setTourGuideRun(false);
        fetchAndUpdateNavigation('disable_transaction_guide');
      }
    }

    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      // Need to set our running state to false, so we can restart if we click start again.
      setTourGuideRun(false);
    }
    const lastStepIndex = size - index;
    if ([ACTIONS.CLOSE].includes(action) || [EVENTS.TOUR_END].includes(type)) {
      if (lastStepIndex > 1) {
        setTourStepIndex(size - 1);
      } else {
        setTourGuideRun(false);
        fetchAndUpdateNavigation('disable_transaction_guide');
      }
    }
  };

  useEffect(() => {
    if (
      tourGuide?.dashboard_guide?.tour_guide &&
      tourGuide?.dashboard_guide?.dashboard_modules?.disable_transaction_guide
    ) {
      setTourGuideRun(!tourGuideRun);
      setTourGuideSteps(TOUR_TRANSACTION_CONTENT);
    }
  }, [tourGuide]);

  return (
    <>
      <div
        style={{
          padding: '10px 24px'
        }}
      >
        <Joyride
          callback={handleJoyrideCallback}
          continuous
          run={tourGuideRun}
          steps={tourGuideSteps}
          scrollOffset={64}
          showSkipButton
          styles={{
            options: {
              primaryColor: 'var(--color-fill-alter)',
              zIndex: 100
            }
          }}
          tooltipComponent={joyrideCustomTooltip}
          stepIndex={tourStepIndex}
        />
        {isTransacationsAllowed ? (
          isTransactionSummary ? (
            <TransactionSummaryCard transactionSummary={transactionSummary} />
          ) : (
            <CustomSkeleton />
          )
        ) : null}
        <Space
          direction="horizontal"
          style={{
            width: '100%',
            paddingBottom: '10px',
            justifyContent: 'space-between',
            marginTop: '14px'
          }}
        >
          <Space>
            {isTransacationsAllowed ? (
              <EbzPopover
                placement="bottomLeft"
                content={
                  <EbzFilterGroup
                    searchParams={TRANSACTION_SEARCH_LIST}
                    filters={TRANSACTION_FILTER_LIST}
                    apply={applyFiltersCallback}
                    clear={clearFiltersCallback}
                  />
                }
                trigger="click"
                overlayStyle={{
                  width: '400px'
                }}
                overlayClassName="ebz-filters-popover"
              >
                <EbzButton
                  size="large"
                  onClick={() => {
                    trackingService.trackEvent(EVENT_NAME.BUTTON_CLICK, {
                      name: 'Search & filter'
                    });
                  }}
                  className="ebz-filters-btn"
                >
                  Search & Filter <DownOutlined />
                </EbzButton>
              </EbzPopover>
            ) : (
              <Button type="primary" size="large" disabled>
                Search & Filter <DownOutlined />
              </Button>
            )}
          </Space>
        </Space>
        {isTransacationsAllowed ? (
          <EbzTable
            columns={columns}
            rowKey={(r) => `${r?.actual_int_id}_transaction`}
            dataSource={transactions?.length ? transactions : []}
            loading={loading}
            scroll={{
              x: 1000,
              y: 500
            }}
            onChange={handleTableChange}
            pagination={false}
            size="middle"
            fetchTransactions={fetchTransactions}
            newPagination={{
              nextCursor,
              prevCursor,
              pageNumber
            }}
          />
        ) : (
          <div
            style={{
              position: 'relative'
            }}
          >
            <img src={transactionsBlur} alt="CUstom Report Table" style={{ maxWidth: '100%' }} />
            <div
              style={{
                position: 'absolute',
                textAlign: 'center',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)'
              }}
            >
              <Typography.Title level={4}>
                Explore trends, search through & export transactions of your referrals from the
                dashboard on the Premium Plan.
              </Typography.Title>
              <Button type="primary" onClick={togglePlanModal}>
                View Plans
              </Button>
            </div>
          </div>
        )}
      </div>
      <TransactionDetailsDrawer
        viewMoreModal={viewMoreModal}
        currentView={currentView}
        productList={productList}
        handleViewMore={handleViewMore}
      />
    </>
  );
}

export default TransactionListPage;
